import { AfterViewInit, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTableModule } from 'ng-zorro-antd/table';
import { Subject, takeUntil } from 'rxjs';
import { UserLoggedService } from 'src/app/core/services/userLogged.service';
import { MachineDto } from 'src/app/shared/dto/machine.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { isValidIpAddress } from 'src/app/shared/utils/utils';
import { MachineService } from '../../services/machine.service';
import { NzFormDirective, NzFormItemComponent, NzFormControlComponent, NzFormModule } from 'ng-zorro-antd/form';
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { DRAWER_WIDTH_CHANGE_PLACEMENT } from 'src/app/core/constants/components';
import { DrawerMode } from 'src/app/core/enums/drawer-mode';
import { MachineStatesEnum } from 'src/app/shared/enums/machine-states.enum';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DefaultComponent } from 'src/app/shared/services/view.service';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-machines',
    standalone: true,
    imports: [
        CommonModule,
        NzPageHeaderModule,
        NzSpaceModule,
        NzDividerModule,
        NzDrawerModule,
        NzDatePickerModule,
        FormsModule,
        NzMessageModule,
        NzTableModule,
        NzSelectModule,
        NzInputDirective,
        ReactiveFormsModule,
        NzFormDirective,
        NzRowDirective,
        NzFormItemComponent,
        NzColDirective,
        NzFormControlComponent,
        NzInputDirective,
        NzFormModule,
        NzIconModule,
        NzToolTipModule,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './machines.component.html',
    styleUrl: './machines.component.scss'
})
export class MachinesComponent extends DefaultComponent implements OnInit, AfterViewInit, OnDestroy {

    machines: MachineDto[] = [];
    machineStatesEnum = MachineStatesEnum;
    fieldsReadOnly: boolean = true;
    drawerTitle: string = 'Placeholder';
    drawerMode!: DrawerMode;
    drawerVisible: boolean = false;
    machineSelected: MachineDto = new MachineDto();
    createdAt: Date = new Date();
    updatedAt: Date = new Date();
    currentUser: UserDto = new UserDto();
    isLoading: boolean = true;
    isSaving: boolean = false;
    
    innerWidth: number = 0;
    DRAWER_WIDTH_CHANGE_PLACEMENT = DRAWER_WIDTH_CHANGE_PLACEMENT;

    validateForm: FormGroup<{
        name: FormControl<string>;
        ip: FormControl<string>;
        queueName: FormControl<string>;
    }>;

    private readonly machineService = inject(MachineService);
    private readonly message = inject(NzMessageService);
    private readonly userLoggedService = inject(UserLoggedService);
    private readonly fb = inject(NonNullableFormBuilder);

    private unsubscribe$ = new Subject<void>();

    constructor() {
        super('machines', '');
        this.validateForm = this.fb.group({
            name: ['', [Validators.required]],
            ip: ['', [Validators.required, Validators.pattern('(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?).(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?).(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?).(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)')]],
            queueName: ['', [Validators.required]]
        });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.innerWidth = window.innerWidth;
    }

    async ngOnInit() {
        this.currentUser = this.userLoggedService.userLogged;
        this.innerWidth = window.innerWidth;

        await this.machineService.setupWebSocket();

        this.machineService.machines$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: (machines: MachineDto[]) => {
                    this.machines = machines;
                    this.machineSelected = this.machines.find(machine => machine.id === this.machineSelected.id) || new MachineDto();
                },
                error: () => {
                    this.message.error('Error cargando máquinas');
                }
            });

        this.isLoading = false;
    }

    ngAfterViewInit() {
        this.innerWidth = window.innerWidth;
    }

    ngOnDestroy(): void {
        this.machineService.disconnectWebSocket();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    createMachine() {
        this.machineSelected = { id: 0, name: '', ip: '', queueName: '', createdAt: new Date(), updatedAt: new Date(), createdBy: 0, updatedBy: 0, state: MachineStatesEnum.AVAILABLE };
        this.drawerMode = DrawerMode.CREATE;
        this.drawerTitle = 'Creando nueva máquina';
        this.fieldsReadOnly = false;
        this.validateForm.enable();
        this.openDrawer();
    }

    view(data: MachineDto) {
        this.validateForm.patchValue(data);
        this.validateForm.disable();
        this.machineSelected = data;
        this.createdAt = this.machineSelected.createdAt
            ? new Date(this.machineSelected.createdAt)
            : new Date();
        this.updatedAt = this.machineSelected.updatedAt
            ? new Date(this.machineSelected.updatedAt)
            : new Date();
        this.drawerMode = DrawerMode.VIEW;
        this.drawerTitle = 'Viendo máquina ' + this.machineSelected?.name;
        this.fieldsReadOnly = true;
        this.openDrawer();
    }

    edit(data: MachineDto) {
        this.machineSelected = data;
        this.validateForm.patchValue(data);
        this.validateForm.enable();
        this.drawerMode = DrawerMode.EDIT;
        this.drawerTitle = 'Editando máquina ' + this.machineSelected?.name;
        this.fieldsReadOnly = false;
        this.openDrawer();
    }

    setData() {
        // This does nothing, it's just for consistency
    }

    cancelEditing() {
        this.closeDrawer();
    }

    async delete(id: number) {
        try {
            if (window.confirm('¿Estás seguro de que quieres eliminar esta máquina? Esta acción no se puede deshacer.')) {
                const machine: MachineDto = await this.machineService.deleteMachine(id);

                if (!machine) {
                    this.message.error(`No se ha encontrado una máquina con id ${id}`);
                }

                const index = this.machines.findIndex((p) => p.id === id);

                if (index < 0) {
                    this.message.create('error', `No se ha podido eliminar la máquina ${machine.name} con id ${id}, máquina no encontrada`);
                }

                this.machines.splice(index, 1);
                const data = [...this.machines];
                this.machines = data;

                this.message.create('success', 'Máquina eliminada correctamente');
            }
        } catch (e: any) {
            this.message.error(e.message);
        }
    }

    openDrawer() {
        this.drawerVisible = true;
    }

    closeDrawer() {
        this.machineSelected = new MachineDto();
        this.validateForm.reset();
        this.drawerVisible = false;
    }

    async submitForm() {
        try {
            this.isSaving = true;

            const customMachineDto: MachineDto = {
                id: this.machineSelected.id,
                name: this.validateForm.controls.name.value ? this.validateForm.controls.name.value : '',
                createdBy: this.drawerMode === DrawerMode.CREATE ? this.currentUser.id : this.machineSelected.createdBy,
                ip: this.validateForm.controls.ip.value ? this.validateForm.controls.ip.value : '',
                queueName: this.validateForm.controls.queueName.value ? this.validateForm.controls.queueName.value : '',
                updatedBy: this.currentUser.id,
                state: this.machineSelected.state
            };

            if (!this.validForm(customMachineDto)) {
                this.isSaving = false;
                return;
            }

            if (this.drawerMode === DrawerMode.CREATE) {
                const createdMachine: MachineDto = await this.machineService.create(customMachineDto);

                if (createdMachine == undefined) {
                    this.isSaving = false;
                    throw new Error('Error creating machine');
                }

                this.machines.push(createdMachine);
                const data = [...this.machines];
                this.machines = data;

                this.isSaving = false;
                this.message.create('success', 'Máquina creada correctamente');
                this.closeDrawer();
            } else if (this.drawerMode === DrawerMode.EDIT) {
                const updatedMachine: MachineDto = await this.machineService.updateMachine(customMachineDto);

                if (updatedMachine == undefined) {
                    this.isSaving = false;
                    throw new Error('Error updating machines');
                }

                const index = this.machines.findIndex((r) => r.id === updatedMachine.id);

                if (index < 0) {
                    this.isSaving = false;
                    this.message.create('error', `No se ha podido actualizar la máquina ${customMachineDto.name} con id ${customMachineDto.id}, máquina no encontrada`);
                    throw new Error('Machine not found');
                }

                this.machines[index] = updatedMachine;
                const data = [...this.machines];
                this.machines = data;

                this.isSaving = false;
                this.message.create('success', 'Máquina actualizada correctamente',);
                this.closeDrawer();
            }
        } catch (e: any) {
            this.isSaving = false;
            this.message.error(`${e.message}`);
        }
    }

    validForm(machine: MachineDto): boolean {
        if (!machine.name || machine.name === '') {
            this.message.create(
                'error',
                'El nombre del proceso de producción es obligatorio'
            );

            return false;
        }

        if (!machine.ip || machine.ip === '') {
            this.message.create(
                'error',
                'La dirección IP de la máquina es obligatoria'
            );

            return false;
        }

        if (!isValidIpAddress(machine.ip)) {
            this.message.create(
                'error',
                'La dirección IP de la máquina no es válida'
            );

            return false;
        }

        return true;
    }
}
